import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Loader2, XCircle } from "lucide-react";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { fetchPaymentStatus } from "@/lib/api";

const VIEW = {
  paid: {
    icon: CheckCircle2,
    tone: "text-[#0f3f2f] bg-gradient-to-b from-[#12876a] to-[#0f3f2f]",
    title: "Pembayaran Berjaya",
    body: "Terima kasih! Pembayaran anda telah disahkan dan tempahan sedang diproses.",
  },
  pending: {
    icon: Clock,
    tone: "text-white bg-gradient-to-b from-[#c79a3b] to-[#8a6a1f]",
    title: "Pembayaran Sedang Diproses",
    body: "Pembayaran anda sedang disahkan. Status akan dikemas kini sebaik sahaja pengesahan diterima.",
  },
  failed: {
    icon: XCircle,
    tone: "text-white bg-gradient-to-b from-[#b3402f] to-[#7d2a1e]",
    title: "Pembayaran Tidak Berjaya",
    body: "Pembayaran anda tidak berjaya. Anda boleh cuba semula dari halaman semakan tempahan.",
  },
};

const toView = (paymentStatus) => {
  if (paymentStatus === "paid") return "paid";
  if (paymentStatus === "failed" || paymentStatus === "expired") return "failed";
  return "pending";
};

export default function BayaranHasilPage() {
  const [params] = useSearchParams();
  const orderReference = params.get("order_id") || params.get("order") || "";
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("pending");
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    if (!orderReference) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    // Authoritative status from backend — query-string is never trusted.
    fetchPaymentStatus(orderReference)
      .then((d) => {
        if (!active) return;
        setView(toView(d.payment_status));
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setNotFound(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orderReference]);

  const cfg = VIEW[view];
  const Icon = cfg?.icon || Clock;

  return (
    <div className="min-h-screen flex flex-col bg-[#faf7f0]">
      <Header />
      <main className="flex-1 max-w-2xl w-full mx-auto px-4 sm:px-6 py-12">
        <div className="panel p-6 sm:p-9" data-testid="bayaran-hasil-page">
          {loading ? (
            <div
              className="grid place-items-center py-14"
              data-testid="bayaran-loading"
            >
              <Loader2 strokeWidth={2} className="h-6 w-6 animate-spin text-[#0f3f2f]" />
            </div>
          ) : notFound ? (
            <div data-testid="bayaran-not-found">
              <h1 className="t-h2">Tempahan Tidak Dijumpai</h1>
              <p className="mt-3 t-body text-sm">
                Kami tidak dapat menjumpai tempahan ini. Sila semak pautan anda.
              </p>
              <Link to="/" className="btn-primary mt-6 inline-flex">
                Kembali ke Laman Utama
              </Link>
            </div>
          ) : (
            <div data-testid={`bayaran-result-${view}`}>
              <span
                className={`grid place-items-center h-12 w-12 rounded-2xl ${cfg.tone}`}
              >
                <Icon strokeWidth={1.75} className="h-6 w-6" />
              </span>
              <h1 className="mt-5 t-h2" data-testid="bayaran-title">
                {cfg.title}
              </h1>
              <p className="mt-3 t-body text-sm">{cfg.body}</p>
              <div className="mt-5 rounded-2xl border border-[#c79a3b]/30 bg-gradient-to-b from-[#fdf8ec] to-white p-5">
                <p className="t-label text-[#8a6a1f]">Nombor Tempahan</p>
                <p
                  className="mt-1 font-heading text-xl font-extrabold text-[#0f3f2f]"
                  data-testid="bayaran-order-reference"
                >
                  {orderReference}
                </p>
              </div>
              <Link to="/" className="btn-secondary mt-6 inline-flex">
                Kembali ke Laman Utama
              </Link>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
